import type { Memory, VariousMemoryIdRefs } from '@timelyapp/integration-cloud-api-spec';
import type { Range } from 'components/ui-blocks/click-drag-adjust/ClickDragAdjust';
import { DateTime } from 'luxon';
import type { HourAsCard, HourDraft } from 'types/hour';
import { fromISO, fromMillis } from 'utils/luxon-helpers';

export interface TimeTrackingInfo {
  hour_id: number | null;
  project_color?: string;
  is_directly_tracked: boolean;
  is_indirectly_tracked: boolean;
  is_partially_tracked: boolean;
}

export type MemoryLoggingMeta = {
  tracked_time: TimeTrackingInfo[];
  total_tracked_hours: number;
} & Omit<TimeTrackingInfo, 'hour_id'>;

export type MemoryOrGroupWithTimeTrackingMeta<T> = T & {
  meta: MemoryLoggingMeta;
};

/**
 * Enriches a Memory or MemoryGroup with logging metadata
 * @param memory Memory or MemoryGroup to enrich
 * @param hours Current hour draft to check against
 */
export function enrichMemoryOrGroupWithTimeTrackingMeta<
  T extends Pick<Memory, 'id' | 'started_at' | 'ended_at'> & { v_id?: VariousMemoryIdRefs },
>(memory: T, hours: HourAsCard[]): MemoryOrGroupWithTimeTrackingMeta<T> {
  if (!hours.length) {
    return {
      ...memory,
      meta: {
        tracked_time: [],
        is_directly_tracked: false,
        is_indirectly_tracked: false,
        is_partially_tracked: false,
        project_color: undefined,
        total_tracked_hours: 0,
      } satisfies MemoryLoggingMeta,
    };
  }

  const tracked_time: TimeTrackingInfo[] = [];

  // Check other hours
  hours.forEach(hour => {
    const hourInfo = checkHourTracking(memory, hour);
    if (hourInfo) tracked_time.push(hourInfo);
  });

  const is_directly_tracked = tracked_time.some(info => info.is_directly_tracked);
  const is_indirectly_tracked =
    !is_directly_tracked && tracked_time.some(info => info.is_indirectly_tracked);
  const is_partially_tracked =
    !is_directly_tracked &&
    !is_indirectly_tracked &&
    tracked_time.some(info => info.is_partially_tracked);

  // Prefer project color from directly tracked hour
  const project_color =
    tracked_time.find(info => info.is_directly_tracked)?.project_color ||
    tracked_time[0]?.project_color ||
    undefined;

  return {
    ...memory,
    meta: {
      tracked_time,
      is_directly_tracked,
      is_indirectly_tracked,
      is_partially_tracked,
      project_color,
      total_tracked_hours: tracked_time.length,
    },
  };
}

/**
 * Determines if a memory is tracked (on various levels) by a given hour
 */
function checkHourTracking(
  memory: Pick<Memory, 'id' | 'started_at' | 'ended_at'> & { v_id?: VariousMemoryIdRefs },
  hour: HourDraft | HourAsCard
): TimeTrackingInfo | null {
  const memoryStart = fromISO(memory.started_at).toUTC();
  const memoryEnd = fromISO(memory.ended_at).toUTC();

  // Check direct logging
  const is_directly_tracked = hour.timestamps.some(
    timestamp =>
      timestamp.entry_ids?.includes(memory.id) ||
      (memory.v_id && timestamp.entry_ids?.includes(memory.v_id))
  );

  let is_indirectly_tracked = false;
  let is_partially_tracked = false;

  if (!is_directly_tracked) {
    // Check indirect and partial logging
    hour.timestamps.some(timestamp => {
      const timestampFrom = fromISO(timestamp.from).toUTC();
      const timestampTo = fromISO(timestamp.to).toUTC();

      if (memoryStart >= timestampFrom && memoryEnd <= timestampTo) {
        // Memory is fully contained within timestamp
        is_indirectly_tracked = true;
        return true;
      } else if (memoryStart <= timestampTo && memoryEnd >= timestampFrom) {
        // Memory partially overlaps timestamp
        is_partially_tracked = true;
        return true;
      }
      return false;
    });
  }

  if (!is_directly_tracked && !is_indirectly_tracked && !is_partially_tracked) return null;

  const project_color = 'projectColor' in hour ? hour.projectColor : hour.state?.color;

  return {
    hour_id: hour.id,
    project_color,
    is_directly_tracked,
    is_indirectly_tracked,
    is_partially_tracked,
  };
}

/**
 * @todo move
 */

export function createLiveTimestampRange(hour: HourAsCard): Range {
  const { year, month, day } = fromISO(hour.day).toObject();

  const from = fromMillis(hour.timerRunningSince * 1000)
    .set({ year, month, day })
    .toISO();

  const to = DateTime.now().set({ year, month, day }).toISO();

  return {
    from,
    to,
    hourId: hour.id,
    color: hour.projectColor,
    live: true,
    index: null,
  };
}
